import {useMutation, useQuery} from '@tanstack/react-query';
import {
  addFolder,
  addFolderGroup,
  addTag,
  addTagGroup,
  deleteFolderGroupOrFolder,
  deleteTagGroupOrTag,
  getEditTagOrTagGroup,
  getFolderOrFolderGroup,
  getFoldersList,
  getTagsFoldersViewData,
  getTagsList,
  moveFolderOrFolderGroup,
  moveTagOrTagGroup,
  updateFolderOrFolderGroup,
  updateTagOrTagGroup,
} from '../../api/endpoints/tagsFolderingApi';
import {
  IAddFolderResponse,
  IAddTagGroupResponse,
  IAddTagResponse,
  IGetFolderOrFolderGroupRequestParams,
  IMoveFolderOrFolderGroupRes,
  IMoveTagOrTagGroupResponse,
  IUpdateTagOrTagGroupResponse,
} from '../../api/types/tagFoldering.type';

export const useAddTag = (
  onSuccess?: (data: IAddTagResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddTagResponse, Error, any>({
    mutationFn: addTag,
    mutationKey: ['addTag'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAddFolder = (
  onSuccess?: (data: IAddFolderResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddFolderResponse, Error, any>({
    mutationFn: addFolder,
    mutationKey: ['addFolder'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetTagsList = (
  params: string,
  enabled: boolean,
  gcTime?: number
) => {
  return useQuery({
    queryKey: ['getTagsList', params],
    queryFn: () => getTagsList(params),
    enabled,
    ...(typeof gcTime === 'number' && {gcTime}),
  });
};

export const useGetTagsFoldersViewData = () => {
  return useQuery({
    queryKey: ['getTagsFoldersViewData'],
    queryFn: () => getTagsFoldersViewData(),
  });
};

export const useAddTagGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddTagGroupResponse, Error, any>({
    mutationFn: addTagGroup,
    mutationKey: ['addTagGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetEditTagOrTagGroup = (params: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['getEditTagOrTagGroup', params],
    queryFn: () => getEditTagOrTagGroup(params),
    enabled,
  });
};

export const useEditTagOrTagGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IUpdateTagOrTagGroupResponse, Error, any>({
    mutationFn: updateTagOrTagGroup,
    mutationKey: ['updateTagOrTagGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetFoldersList = (
  params: string,
  enabled: boolean,
  gcTime?: number
) => {
  return useQuery({
    queryKey: ['getFoldersList', params],
    queryFn: () => getFoldersList(params),
    enabled,
    ...(typeof gcTime === 'number' && {gcTime}),
  });
};

export const useDeleteTagGroupOrTag = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deleteTagGroupOrTag,
    mutationKey: ['deleteTagGroupOrTag'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAddFolderGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddTagGroupResponse, Error, any>({
    mutationFn: addFolderGroup,
    mutationKey: ['addFolderGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetFolderOrFolderGroup = (
  params: IGetFolderOrFolderGroupRequestParams,
  enabled = false
) => {
  return useQuery({
    queryKey: ['getFolderOrFolderGroup', params],
    queryFn: () => getFolderOrFolderGroup(params),
    enabled,
  });
};

export const useUpdateFolderOrFolderGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddTagGroupResponse, Error, any>({
    mutationFn: updateFolderOrFolderGroup,
    mutationKey: ['updateFolderOrFolderGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteFolderGroupOrFolder = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deleteFolderGroupOrFolder,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useMoveFolderOrFolderGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IMoveFolderOrFolderGroupRes, Error, any>({
    mutationFn: moveFolderOrFolderGroup,
    mutationKey: ['moveFolderOrFolderGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useMoveTagOrTagGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IMoveTagOrTagGroupResponse, Error, any>({
    mutationFn: moveTagOrTagGroup,
    mutationKey: ['moveTagOrTagGroup'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
